// ============================================================
// GGPlayer v4 — Main Player Class
// ============================================================
import type { PlayerConfig, QualityLevel, ResolvedConfig, PlayerEvents, IPlugin, PluginContext, PlayerError } from './core/types';
import { EventBus } from './core/EventBus';
import { Store } from './core/Store';
import { Logger } from './core/Logger';
import { UIRegistry } from './core/UIRegistry';
import { resolveConfig, resolveStreamUrl, extractQueryParams, getSavedLatencyMode, saveSettings } from './core/Config';
import { detectPlatform } from './platform/detect';
import { createEngine, isDirectFileSource } from './engine/EngineFactory';
import { HlsEngine } from './engine/HlsEngine';
import { LLHlsEngine } from './engine/LLHlsEngine';
import { detectStreamType } from './engine/detectStreamType';
import type { IEngine } from './engine/IEngine';
import type { IPlayerShell } from './ui-react/IPlayerShell';
import { ReactShellAdapter } from './ui-react/ReactShellAdapter';
import { DebugPlugin } from './plugins/debug/DebugPlugin';
import { CdnTokenRecovery } from './core/CdnTokenRecovery';

export class GGPlayer {
  private eventBus: EventBus;
  private store: Store;
  private logger: Logger;
  private uiRegistry: UIRegistry;
  private config: ResolvedConfig;
  private engine: IEngine | null = null;
  private plugins: IPlugin[] = [];
  private shell: IPlayerShell | null = null;
  private streamSource: import('./core/types').StreamSource | null = null;
  private streamQueryParams = '';
  private cdnTokenRecovery: CdnTokenRecovery | null = null;
  /** Whether this stream supports LL-HLS (detected or forced) */
  private _llhlsAvailable = false;
  /** Deferred autoplay: true when autoplay was requested but tab was hidden */
  private _deferredAutoplay = false;
  /** Stored reference for cleanup */
  private _visibilityHandler: (() => void) | null = null;
  /** Set to true by destroy() to abort any in-flight async init */
  private _destroyed = false;

  constructor(playerConfig: PlayerConfig) {
    this.eventBus = new EventBus();
    this.logger = new Logger('warn');
    this.uiRegistry = new UIRegistry();
    this.config = resolveConfig(playerConfig);
    this.store = new Store({
      streamKey: this.config.streamKey,
      volume: this.config.volume,
      isMuted: this.config.muted,
      quality: this.config.quality,
      isLowLatency: false,
      streamType: 'unknown',
    });
    this.init();
  }

  // --- Public API ---

  play(): void {
    this.engine?.play();
  }

  pause(): void {
    this.cdnTokenRecovery?.cancel();
    this.engine?.pause();
  }

  stop(): void {
    this.cdnTokenRecovery?.cancel();
    this.engine?.stop();
  }

  destroy(): void {
    this._destroyed = true;
    this.cdnTokenRecovery?.destroy();
    this.cdnTokenRecovery = null;
    for (const plugin of this.plugins) {
      plugin.destroy();
    }
    this.plugins = [];
    this.engine?.destroy();
    this.engine = null;
    this.shell?.destroy();
    this.shell = null;
    // Remove visibility listener
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this._deferredAutoplay = false;
    this.uiRegistry.destroy();
    this.store.destroy();
    this.eventBus.removeAll();
  }

  get isPlaying(): boolean {
    return this.store.getState().isPlaying;
  }

  get currentTime(): number {
    return this.store.getState().currentTime;
  }

  get volume(): number {
    return this.store.getState().volume;
  }

  set volume(value: number) {
    const clamped = Math.round(Math.max(0, Math.min(1, value)) * 100) / 100;
    this.engine?.setVolume(clamped);
    this.store.setState({ volume: clamped, isMuted: clamped === 0 });
    saveSettings({ volume: clamped, muted: clamped === 0 });
  }

  set muted(value: boolean) {
    if (this.engine) {
      this.engine.setVolume(value ? 0 : this.store.getState().volume);
    }
    this.store.setState({ isMuted: value });
    this.eventBus.emit('muted:change', value);
    saveSettings({ muted: value });
  }

  get quality(): QualityLevel {
    return this.store.getState().quality;
  }

  get qualities(): QualityLevel[] {
    return this.store.getState().availableQualities;
  }

  setQuality(level: QualityLevel): void {
    this.engine?.setQuality(level);
    saveSettings({ quality: this.store.getState().quality });
  }

  /** Directly set player state. Intended for dev/testing — use with care. */
  setState(state: Partial<import('./core/types').PlayerState>): void {
    this.store.setState(state);
  }

  get latency(): number {
    return this.engine?.getLatency() ?? 0;
  }

  get isLowLatency(): boolean {
    return this.store.getState().isLowLatency;
  }

  /** Current latency mode: 'low' (LL-HLS) or 'normal' (HLS) */
  get latencyMode(): 'low' | 'normal' {
    return this.store.getState().latencyMode;
  }

  /** Whether LL-HLS was ever available for the current stream */
  get llhlsAvailable(): boolean {
    return this.store.getState().llhlsAvailable;
  }

  /**
   * Switch between low-latency and normal latency mode.
   * @param mode 'low' for LL-HLS, 'normal' for standard HLS
   */
  switchLatencyMode(mode: 'low' | 'normal'): boolean {
    const current = this.store.getState().latencyMode;
    if (mode === current) return true;

    if (mode === 'normal') {
      this.switchEngine('hls');
    } else if (mode === 'low') {
      if (!this._llhlsAvailable) {
        this.logger.warn('LL-HLS is not available for this stream');
        return false;
      }
      this.switchEngine('ll-hls');
    }
    return true;
  }

  get viewers(): number {
    return this.store.getState().viewers;
  }

  /** Subscribe to player events. Returns unsubscribe function. */
  on<K extends keyof PlayerEvents>(
    event: K,
    handler: PlayerEvents[K] extends void ? () => void : (data: PlayerEvents[K]) => void,
  ): () => void {
    return this.eventBus.on(event, handler as any);
  }

  off<K extends keyof PlayerEvents>(event: K, handler: Function): void {
    this.eventBus.off(event, handler);
  }

  /** Emit a player event. Used for plugins and backward compatibility. */
  emit<K extends keyof PlayerEvents>(
    event: K,
    ...args: PlayerEvents[K] extends void ? [] : [PlayerEvents[K]]
  ): void {
    this.eventBus.emit(event, ...args as any);
  }

  /** Register a plugin at runtime. */
  use(plugin: IPlugin): void {
    plugin.init(this.createPluginContext());
    this.plugins.push(plugin);
  }

  /** Set log level for debugging. */
  setLogLevel(level: 'debug' | 'info' | 'warn' | 'error' | 'silent'): void {
    this.logger.setLevel(level);
  }

  // --- Backward compatibility ---

  onStart(cb: () => void): void { this.eventBus.on('playing', cb); }
  onStop(cb: () => void): void { this.eventBus.on('stopped', cb); }
  onViewers(cb: (count: number) => void): void { this.eventBus.on('viewers:update', cb); }
  clean(): void { this.engine?.clean?.(); }

  // --- Private ---

  private async init(): Promise<void> {
    this.setupVisibilityTracking();
    const capabilities = detectPlatform();
    this.logger.info('Platform:', capabilities);

    const adapter = new ReactShellAdapter(
      this.config.container,
      this.eventBus,
      this.store,
      this.config,
      () => this.engine,
      this.uiRegistry,
      capabilities,
    );
    await adapter.waitForRoot();
    if (this._destroyed) return;
    this.shell = adapter;

    // Resolve stream URL
    const streamUrl = resolveStreamUrl(this.config);
    const queryParams = extractQueryParams(streamUrl);
    const source = this.config.sources ?? this.config.apiData?.sources ?? { master: streamUrl };
    this.streamSource = source;
    this.streamQueryParams = queryParams;

    // A saved user preference is evaluated against the manifest, not exposed as config.
    let detectedLowLatency = false;
    const savedLatencyMode = getSavedLatencyMode();
    const hasSavedLatencyMode = savedLatencyMode !== undefined;
    if ((this.config.lowLatency === 'auto' || hasSavedLatencyMode) && !isDirectFileSource(this.config)) {
      const playlistUrl = source.master || source.source || streamUrl;
      this.logger.warn('Auto-detecting stream type from manifest:', playlistUrl);
      detectedLowLatency = await detectStreamType(playlistUrl);
      if (this._destroyed) return;
      this.logger.warn(`Stream type detected: ${detectedLowLatency ? 'LL-HLS (low-latency)' : 'HLS (standard)'}`);
    }

    const initialLowLatency = hasSavedLatencyMode
      ? savedLatencyMode === 'low' && detectedLowLatency
      : this.config.lowLatency;
    const engineConfig = hasSavedLatencyMode
      ? { ...this.config, lowLatency: initialLowLatency }
      : this.config;

    // Create engine (with detection result for 'auto' mode)
    this.engine = createEngine(engineConfig, capabilities, {
      eventBus: this.eventBus,
      store: this.store,
      logger: this.logger,
      autoplay: this.config.autoplay,
    }, detectedLowLatency);
    this._llhlsAvailable = this.engine.isLowLatency || detectedLowLatency;
    this.store.setState({ engineName: this.engine.name, isLowLatency: this.engine.isLowLatency, latencyMode: this.engine.isLowLatency ? 'low' : 'normal', llhlsAvailable: this._llhlsAvailable });

    // Attach video element to the shell root (behind overlays/controls)
    this.engine.attach(this.shell.getRoot());

    // Init plugins
    const pluginCtx = this.createPluginContext();

    if (this.config.showDebug) {
      const debug = new DebugPlugin();
      debug.init(pluginCtx);
      this.plugins.push(debug);
    }

    for (const plugin of this.config.plugins) {
      plugin.init(pluginCtx);
      this.plugins.push(plugin);
    }

    this.cdnTokenRecovery = new CdnTokenRecovery(pluginCtx, async (updatedSource, updatedQuery, restart) => {
      this.streamSource = updatedSource;
      this.streamQueryParams = updatedQuery;
      if (restart && this.config.lowLatency === 'auto') {
        // The initial probe may have received 403 with the old credentials.
        const detected = await detectStreamType(updatedSource.master ?? updatedSource.source ?? '');
        if (this._destroyed) return;
        this._llhlsAvailable = detected;
        const lowLatency = detected && getSavedLatencyMode() !== 'normal';
        if (this.engine && this.engine.isLowLatency !== lowLatency) {
          const volume = this.store.getState().isMuted ? 0 : this.store.getState().volume;
          this.store.setState({ isSwitching: true });
          this.engine.destroy();
          this.engine = createEngine({ ...this.config, lowLatency }, detectPlatform(), {
            eventBus: this.eventBus, store: this.store, logger: this.logger, autoplay: false,
          }, detected);
          this.engine.attach(this.shell!.getRoot());
          this.engine.setVolume(volume);
          this.store.setState({ isSwitching: false, engineName: this.engine.name,
            isLowLatency: this.engine.isLowLatency, latencyMode: this.engine.isLowLatency ? 'low' : 'normal' });
        }
        this.store.setState({ llhlsAvailable: detected });
      }
      if (!restart && this.engine?.updateSourceAuth) {
        this.engine.updateSourceAuth(updatedSource, updatedQuery);
      } else {
        this.engine?.load(updatedSource, updatedQuery);
      }
    });

    // Load stream
    this.engine.load(source, queryParams);

    // Apply initial volume/muted from config (video element defaults to 1.0)
    this.engine.setVolume(this.config.muted ? 0 : this.config.volume);

    // Persist volume/quality/muted changes
    this.eventBus.on('volume:change', (vol) => {
      saveSettings({ volume: vol, muted: vol === 0 });
    });
    this.eventBus.on('muted:change', (muted: boolean) => {
      saveSettings({ muted });
    });
    this.eventBus.on('quality:changed', (q: QualityLevel) => saveSettings({ quality: q }));

    // LL-HLS → HLS fallback: if latency is persistently unstable, switch engine
    if (this.config.latencyFallback) {
      this.eventBus.on('error', (err: PlayerError) => {
        if (this.engine?.isLowLatency && err.code === 4001 && !err.fatal) {
          this.logger.warn('Auto-fallback: LL-HLS latency unstable, switching to standard HLS');
          this.switchEngine('hls');
          this.eventBus.emit('latency:fallback');
        }
      });
    }

    // Listen for manual latency mode switch from UI
    this.eventBus.on('latency:mode', (mode: 'low' | 'normal') => {
      if (this.switchLatencyMode(mode)) {
        saveSettings({ latencyMode: mode });
      }
    });

    // VOD seek: timeline emits 'seek:to' with an absolute position in seconds
    this.eventBus.on('seek:to', (time: number) => {
      const video = this.engine?.getVideoElement();
      if (video) {
        const dur = this.store.getState().duration;
        video.currentTime = Math.max(0, dur > 0 ? Math.min(time, dur) : time);
      }
    });

    // Set API data if provided
    if (this.config.apiData) {
      this.store.setState({ isOnline: this.config.apiData.channel_status === 'online' });
      if (this.config.apiData.viewers) {
        this.store.setState({ viewers: this.config.apiData.viewers });
      }
      this.eventBus.emit('api:data', this.config.apiData);
    }

    this.logger.info(`GGPlayer v4 initialized — engine: ${this.engine.name}, stream: ${this.config.streamKey}`);
    this.config.onReady?.();

    // Deferred autoplay: play immediately if tab is visible, defer if hidden
    if (this.config.autoplay && !this.config.apiData?.adult) {
      if (this.store.getState().windowIsActive) {
        this.play();
      } else {
        this._deferredAutoplay = true;
      }
    }
  }

  private switchEngine(target: 'hls' | 'll-hls'): void {
    if (!this.shell) return;
    const oldEngine = this.engine;
    if (!oldEngine) return;

    // Don't switch to same type
    if (target === 'hls' && !oldEngine.isLowLatency) return;
    if (target === 'll-hls' && oldEngine.isLowLatency) return;

    // Guard against re-entrant switch (e.g. rapid toggle clicks)
    if (this.store.getState().isSwitching) return;

    this.logger.warn(`Switching engine: ${oldEngine.name} → ${target}`);

    const { volume, isPlaying: hasStarted, loading } = this.store.getState();
    // play() can still be waiting for a live seek when fallback begins. A
    // non-paused loading element has playback intent even before `playing`.
    const isPlaying = hasStarted || (loading && oldEngine.getVideoElement()?.paused === false);
    this.store.setState({ isSwitching: true });

    // doSwap is defined after newEngine/newVideo are created so it can close over
    // them, but onManifestReady (passed in ctx) references it via the mutable slot
    // below — safe because onManifestReady is only ever called after load() returns.
    let doSwap: (() => void) | undefined;
    let switchTimer: ReturnType<typeof setTimeout> | undefined;
    let revealNewVideo: (() => void) | undefined;
    let isBackgroundPlayback = true;
    let suppressLoadingUntil = Number.POSITIVE_INFINITY;

    // autoplay: false — MANIFEST_PARSED will NOT call hls.startLoad() or play().
    // We trigger both manually inside doSwap, after the old engine is destroyed,
    // so that zero segment XHRs are issued on the new engine during the preload
    // window. This eliminates the burst of canceled requests seen in devtools.
    const ctx = {
      eventBus: this.eventBus,
      store: this.store,
      logger: this.logger,
      autoplay: false,
      onManifestReady: () => queueMicrotask(() => doSwap?.()),
      isBackgroundPlayback: () => isBackgroundPlayback,
      suppressLoadingState: () => isBackgroundPlayback || Date.now() < suppressLoadingUntil,
      onInitialLiveLatencyAligned: () => revealNewVideo?.(),
    };

    // Create new engine — old engine keeps playing while manifest is being fetched
    const newEngine: IEngine = target === 'll-hls'
      ? new LLHlsEngine(ctx)
      : new HlsEngine(ctx);

    // Attach new <video> as an invisible overlay so old video stays visible
    newEngine.attach(this.shell.getRoot());
    const newVideo = newEngine.getVideoElement();
    if (newVideo) {
      newVideo.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;opacity:0;z-index:-1;pointer-events:none';
      // Keep the warming engine silent without touching the shared player store.
      newVideo.muted = true;
    }

    // Fetch only the manifest — no segment downloads yet (autoplay: false)
    if (this.streamSource) {
      newEngine.load(this.streamSource, this.streamQueryParams);
    }
    let done = false;

    doSwap = () => {
      if (done) return;
      done = true;
      clearTimeout(switchTimer);

      // Phase 1: swap the engine reference. The old video and audio remain active
      // while the new engine buffers and, for LL-HLS, finishes startup alignment.
      // The new video stays invisible (position:absolute;opacity:0;z-index:-1) while
      // it buffers so the old stream remains on screen without interruption.
      this.engine = newEngine;
      const isLL = newEngine.isLowLatency;
      const mode = isLL ? 'low' : 'normal';
      this.store.setState({ engineName: newEngine.name, isLowLatency: isLL, latencyMode: mode });
      this.logger.info(`Engine switched to ${newEngine.name} (${mode} latency)`);

      if (!isPlaying || !newVideo) {
        // Not playing (or test env without a video element) — reveal immediately and clean up.
        if (newVideo) {
          newVideo.style.cssText = 'width:100%;height:100%';
        }
        isBackgroundPlayback = false;
        suppressLoadingUntil = Date.now() + 600;
        newEngine.setVolume(volume);
        oldEngine.destroy();
        this.store.setState({ isSwitching: false, loading: false });
        if (isPlaying) {
          newEngine.play();
        }
        return;
      }

      // Phase 2: wait for the first frame of the new stream before tearing down the old one.
      // This eliminates the black-screen flash caused by destroying the old <video> before
      // the new one has anything to paint.
      let revealed = false;
      let hasFirstFrame = false;
      let latencyAligned = !isLL;
      let stabilityTimer: ReturnType<typeof setTimeout> | undefined;
      const LL_HANDOFF_STABILITY_MS = 600;

      const commitReveal = () => {
        if (revealed) return;
        revealed = true;
        clearTimeout(revealTimer);
        clearTimeout(stabilityTimer);
        newVideo.removeEventListener('playing', onFirstFrame);
        newVideo.removeEventListener('waiting', onWaiting);
        isBackgroundPlayback = false;
        suppressLoadingUntil = Date.now() + 600;
        newEngine.setVolume(volume);
        // Reveal the new video with the correct sizing that attach() expected
        newVideo.style.cssText = 'width:100%;height:100%';
        // Destroy old engine now that the new one is already rendering a stable frame
        oldEngine.destroy();
        // Restore isPlaying — oldEngine.destroy() → stop() set it false
        this.store.setState({ isPlaying: true, isSwitching: false, loading: false });
      };

      const tryReveal = () => {
        if (!hasFirstFrame || !latencyAligned) return;
        if (revealed) return;
        if (!isLL) {
          commitReveal();
          return;
        }
        clearTimeout(stabilityTimer);
        stabilityTimer = setTimeout(commitReveal, LL_HANDOFF_STABILITY_MS);
      };

      const onFirstFrame = () => {
        hasFirstFrame = true;
        tryReveal();
      };

      const onWaiting = () => {
        if (!isLL || revealed) return;
        hasFirstFrame = false;
        clearTimeout(stabilityTimer);
      };

      revealNewVideo = () => {
        latencyAligned = true;
        tryReveal();
      };

      // Safety net: reveal even if the expected readiness signal is missed.
      const revealTimer = setTimeout(commitReveal, isLL ? 4_000 : 3_000);
      newVideo.addEventListener('playing', onFirstFrame);
      newVideo.addEventListener('waiting', onWaiting);

      // Begin segment loading — new engine is now the active one
      newEngine.play();
    };

    if (!newVideo) {
      // No video element (e.g. test environment) — swap immediately
      doSwap();
    } else {
      // Safety net: if manifest never parses (e.g. network issue), force the swap
      // after 8 s so isSwitching doesn't get stuck and play() is still attempted.
      switchTimer = setTimeout(doSwap, 8_000);
    }
  }

  private createPluginContext(): PluginContext {
    return {
      eventBus: this.eventBus,
      store: this.store,
      config: this.config,
      logger: this.logger,
      getEngine: () => this.engine,
      uiRegistry: this.uiRegistry,
    };
  }

  private setupVisibilityTracking(): void {
    if (typeof document === 'undefined') return;

    // Set initial state
    const isVisible = !document.hidden;
    this.store.setState({ windowIsActive: isVisible });

    // Listen for visibility changes
    this._visibilityHandler = () => {
      const visible = !document.hidden;
      this.store.setState({ windowIsActive: visible });
      this.eventBus.emit('visibility:change', visible);

      // Resume deferred autoplay when tab becomes visible
      if (visible && this._deferredAutoplay) {
        this._deferredAutoplay = false;
        this.play();
      }
    };

    document.addEventListener('visibilitychange', this._visibilityHandler);
  }
}
